<template>
    <form @submit="onSubmit" method="post" class="m-4" style="position: relative">

        <UserNotificationDialog :message="userNotification" @closed="onUserNotificationClose"></UserNotificationDialog>

        <div class="row" style="height: 7rem">
            <div class="col" v-if="userSession.shouldRemember">
                <div class="text-center" style="font-size: 1.6rem">{{ this.$t('welcomeBack') }}</div>
                <div class="text-center" style="font-size: 1.6rem; color: #c6c;">{{ this.userSession.name }}</div>
            </div>
        </div>

        <div v-if="loading" class="row justify-content-center position-absolute pt-3" style="left: 0; right: 0">
            <div
                class="spinner-border"
                role="status"
                style="width: 10rem; height: 10rem; z-index: 1"
            ></div>
        </div>

        <div class="row justify-content-center py-4">
            <label for="username" class="sr-only">
                Username
            </label>
            <input
                type="text"
                id="username"
                autocomplete="off"
                tabindex="1"
                class="form-control form-control-lg col"
                :disabled="loading || userSession.shouldRemember"
                v-model="username"
                :placeholder="this.$t('username')"
            >
        </div>

        <div class="row justify-content-center py-4">
            <label for="password" class="sr-only">
                Password
            </label>
            <input
                type="password"
                id="password"
                autocomplete="off"
                tabindex="2"
                class="form-control form-control-lg col"
                :disabled="loading || userSession.shouldRemember"
                v-model="password"
                :placeholder="this.$t('password')"
            >
        </div>

        <div class="row justify-content-center align-items-center py-4">
            <div :hidden="!error" style="color: red;">{{ this.error }}</div>
        </div>

        <div class="row justify-content-end">
            <router-link to="reset_password" :is="loading ? 'span' : 'router-link'">
                {{ $t('forgottenPassword') }}
            </router-link>
        </div>

        <div class="row justify-content-between py-2">
            <div class="col" v-if="userSession.shouldRemember">
                {{ $t('notYou') }}
                <a href="#"
                   id="remove-account-link"
                   tabindex="5"
                   v-on:click="clear"
                   :disabled="loading">
                    {{ $t('changeCredentials') }}
                </a>
            </div>
            <div class="col" v-else>
                <div class="custom-control custom-checkbox" style="user-select: none">
                    <input
                        type="checkbox"
                        class="custom-control-input"
                        id="rememberMe"
                        tabindex="3"
                        :disabled="loading"
                        v-model="rememberMe">
                    <label for="rememberMe"
                           style="cursor: pointer; user-select: none;"
                           class="custom-control-label">
                        {{ $t('rememberMe') }}
                    </label>
                </div>
            </div>

            <button
                type="submit"
                id="login"
                tabindex="4"
                :disabled="loading"
                class="btn btn-primary col">
                {{ $t('logIn') }}
            </button>
        </div>

    </form>

</template>

<script>
import UserSession from "@/service/UserSession";
import AuthenticationService from "@/service/AuthenticationService";
import UserService from "@/service/UserService";
import UserNotificationDialog from "@/components/Users/UserNotificationDialog";
import {JwtToken} from "@/utils/jwt";

export default {
    components: {
        UserNotificationDialog,
    },
    data() {
        const userSession = new UserSession(localStorage);

        return {
            userSession: userSession,
            userService: new UserService(),
            authenticationService: new AuthenticationService(userSession),
            username: "",
            password: "",
            rememberMe: false,
            loading: false,
            continueUrl: null,
            userNotification: null,
            error: "",
        };
    },
    created() {
        if (this.userSession.shouldRemember) {
            this.username = this.userSession.username;
            this.password = "some_dummy_password";
        }
    },
    methods: {
        onSubmit: function (e) {
            e.preventDefault();

            this.loading = true;
            this.error = false;

            let authentication = this.userSession.shouldRemember
                ? this.authenticationService.refresh()
                : this.authenticationService.authenticate(
                    this.username,
                    this.password,
                    this.userSession.shouldRemember || this.rememberMe,
                    this.$route.query.continue
                );

            authentication
                .then(url => {
                    this.continueUrl = url;
                    this.userService.get(JwtToken.fromLocalStorage().userId())
                        .then((user) => {
                            const notification = user.notifications.find(n => !n.seenAt)
                            if (notification) {
                                this.userNotification = notification.message;
                            } else {
                                this.continueLogin();
                            }
                            this.loading = false;
                        });
                }, this.showErrors);
        },
        showErrors(errors) {
            if (Array.isArray(errors) && errors.length > 0) {
                const error = errors[0];

                if (error.code === 'UserMustChangePassword') {
                    this.$router.push(`/change_password?username=${this.username}&show_warning=true`);
                } else if (error.code === 'IncorrectCredentials') {
                    this.error = this.$t('IncorrectCredentials');
                } else if (error.code === 'UserNotAssociatedToOrganization') {
                    this.error = this.$t('UserNotAssociatedToOrganization');
                } else {
                    this.error = this.$t('somethingWentWrong');
                }
            } else {
                this.error = this.$t('somethingWentWrong');
            }

            this.loading = false
        },
        clear: function (e) {
            e.preventDefault();

            this.userSession.shouldRemember = false;

            this.password = "";
            this.username = "";
        },
        continueLogin() {
            if (this.userSession.isSystemAdmin) {
                this.$router.push({name: 'LoginAs', query: this.$route.query});
            } else {
                location.href = this.continueUrl;
            }
        },
        onUserNotificationClose() {
            this.userService.clearNotifications(JwtToken.fromLocalStorage().userId())
                .then(() => this.continueLogin());
        }
    }
};
</script>

<style lang="scss">
@import '~bootstrap/scss/bootstrap';
</style>

<i18n lang="yaml" locale="en">
welcomeBack: "Welcome back"
forgottenPassword: "Forgot your password?"
changeCredentials: "Change credentials"
rememberMe: "Remember me"
notYou: "Not you?"
</i18n>

<i18n lang="yaml" locale="dk">
welcomeBack: "Velkommen tilbage"
forgottenPassword: "Glemt din adgangskode?"
changeCredentials: "Skift login"
rememberMe: "Husk mig"
notYou: "Ikke dig?"
</i18n>
